import { useState, useEffect } from 'react'
import { Users, Monitor, Bot, Bell, ArrowRight } from 'lucide-react'
import { Link } from 'react-router-dom'
import { AreaChart, Area, ResponsiveContainer } from 'recharts'
import {
  getAdminAnalytics,
  getAnalyticsTrends,
  listUsers,
  listWorkspaces,
  type AdminAnalytics,
  type TrendPoint,
} from '../../lib/api'
import { CHART_PALETTE } from '../../lib/chart-colors'
import { useCan } from '../../hooks/useUser'

interface Stats {
  users: number
  workspaces: number
}

function Sparkline({ data, dataKey, color }: { data: TrendPoint[]; dataKey: string; color: string }) {
  if (!data || data.length < 2) return null
  return (
    <div className="h-10 w-28">
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart data={data} margin={{ top: 2, right: 2, bottom: 2, left: 2 }}>
          <Area
            type="monotone"
            dataKey={dataKey}
            stroke={color}
            fill={color}
            fillOpacity={0.15}
            strokeWidth={1.5}
            dot={false}
            isAnimationActive={false}
          />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  )
}

export default function AdminOverview() {
  const can = useCan()
  // Each card/action is gated by the permission its target screen requires, so a
  // role-scoped user never sees a control that would 403 on click. Keys mirror
  // OVERVIEW_AREAS / the route guards in App.tsx.
  const canMetrics = can('analytics.read')
  const canWorkspaces = can('apps.read')
  const canUsers = can('users.read')
  const canClaudeCode = can('config.read')
  const canTestNotifications = can('notifications.write')
  const canPlatformInfo = can('platform_settings.read')
  const [stats, setStats] = useState<Stats>({ users: 0, workspaces: 0 })
  const [analytics, setAnalytics] = useState<AdminAnalytics | null>(null)
  const [trends, setTrends] = useState<TrendPoint[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    async function loadStats() {
      try {
        // Each fetch is individually resilient: the Overview dashboard is open
        // to anyone with admin access, but a role-scoped user may lack
        // users.read / workspaces.read / analytics.read. Missing permissions
        // degrade to an empty card rather than failing the whole page.
        const [users, workspaces, analyticsData, trendsData] = await Promise.all([
          listUsers().catch(() => [] as Awaited<ReturnType<typeof listUsers>>),
          listWorkspaces().catch(() => [] as Awaited<ReturnType<typeof listWorkspaces>>),
          getAdminAnalytics('30d').catch(() => null),
          getAnalyticsTrends('7d').catch(() => null),
        ])
        setStats({
          users: users.length,
          workspaces: workspaces.length,
        })
        if (analyticsData) setAnalytics(analyticsData)
        if (trendsData) setTrends(trendsData)
      } catch (err) {
        console.error('Failed to load stats:', err)
      } finally {
        setLoading(false)
      }
    }
    loadStats()
  }, [])

  const metricCards = analytics
    ? [
        {
          label: 'Active Users (DAU)',
          value: analytics.platform.active_users_daily,
          sparklineKey: 'active_users',
          color: CHART_PALETTE[0],
          to: '/admin/analytics/users',
        },
        {
          label: 'Total Apps',
          value: analytics.platform.total_apps,
          sparklineKey: 'total_apps',
          color: CHART_PALETTE[1],
          to: '/admin/analytics/apps',
        },
        {
          label: 'Running Apps',
          value: analytics.platform.running_apps,
          sparklineKey: 'deploys',
          color: CHART_PALETTE[2],
          to: '/admin/analytics/apps',
        },
        {
          label: 'Running Ratio',
          value: `${analytics.platform.running_app_ratio}%`,
          sparklineKey: null,
          color: CHART_PALETTE[3],
          to: '/admin/analytics',
        },
      ]
    : []

  const quickCards = [
    { label: 'Workspaces', value: stats.workspaces, icon: Monitor, href: '/admin/workspaces', show: canWorkspaces },
    { label: 'Users', value: stats.users, icon: Users, href: '/admin/users', show: canUsers },
  ].filter((card) => card.show)

  const quickActions = [
    { label: 'Manage Workspaces', icon: Monitor, href: '/admin/workspaces', show: canWorkspaces },
    { label: 'Manage Users', icon: Users, href: '/admin/users', show: canUsers },
    { label: 'Claude Code Configuration', icon: Bot, href: '/admin/claude-code', show: canClaudeCode },
    { label: 'Notifications & Survey', icon: Bell, href: '/admin/platform-settings', show: canTestNotifications },
  ].filter((action) => action.show)

  return (
    <div>
      <div className="mb-8">
        <h1 className="text-2xl font-bold mb-2">Admin Overview</h1>
        <p className="text-muted-foreground">Platform management and configuration</p>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary"></div>
        </div>
      ) : (
        <>
          {/* Analytics metric cards with sparklines */}
          {canMetrics && analytics && (
            <div className="mb-8">
              <h2 className="text-lg font-semibold mb-4">Platform Metrics</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                {metricCards.map((card) => (
                  <Link
                    key={card.label}
                    to={card.to}
                    className="p-4 rounded-xl border border-border bg-card hover:border-primary/50 transition-colors block"
                  >
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm text-muted-foreground">{card.label}</span>
                      <ArrowRight className="w-3.5 h-3.5 text-muted-foreground" />
                    </div>
                    <div className="flex items-end justify-between gap-3">
                      <span className="text-2xl font-bold">{card.value}</span>
                      {card.sparklineKey && (
                        <Sparkline data={trends} dataKey={card.sparklineKey} color={card.color} />
                      )}
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          )}

          {/* Workspace/User count cards */}
          {quickCards.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
              {quickCards.map((card) => (
                <Link
                  key={card.label}
                  to={card.href}
                  className="p-6 rounded-xl border border-border bg-card hover:border-primary/50 transition-colors block"
                >
                  <div className="flex items-center justify-between mb-4">
                    <card.icon className="w-8 h-8 text-muted-foreground" />
                    <span className="text-2xl font-bold">{card.value}</span>
                  </div>
                  <p className="text-muted-foreground">{card.label}</p>
                </Link>
              ))}
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {quickActions.length > 0 && (
              <div className="p-6 rounded-xl border border-border bg-card">
                <h2 className="text-lg font-semibold mb-4">Quick Actions</h2>
                <div className="space-y-2">
                  {quickActions.map((action) => (
                    <Link
                      key={action.href}
                      to={action.href}
                      className="flex items-center gap-3 p-3 rounded-lg hover:bg-secondary transition-colors"
                    >
                      <action.icon className="w-5 h-5 text-muted-foreground" />
                      <span>{action.label}</span>
                    </Link>
                  ))}
                </div>
              </div>
            )}

            {canPlatformInfo && (
            <div className="p-6 rounded-xl border border-border bg-card">
              <h2 className="text-lg font-semibold mb-4">Platform Info</h2>
              <div className="space-y-3 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Platform</span>
                  <span>Code Concierge v1.0</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Workspace Image</span>
                  <span>cc-workspace:latest</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Memory Limit</span>
                  <span>4GB per workspace</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">CPU Limit</span>
                  <span>2 cores per workspace</span>
                </div>
              </div>
            </div>
            )}
          </div>
        </>
      )}
    </div>
  )
}
